import React from 'react';
import { Text } from '../Typography';
import { Box } from '../Layout';

export interface CharacterCounterProps {
  /** Current text length */
  currentLength: number;
  /** Maximum allowed length */
  maxLength?: number;
  /** Warning threshold (percentage of max) */
  warningThreshold?: number;
  /** Show remaining characters instead of current/max */
  showRemaining?: boolean;
  /** Custom styling */
  className?: string;
  /** Additional content to show when over limit */
  overLimitMessage?: string;
}

export const CharacterCounter: React.FC<CharacterCounterProps> = ({
  currentLength,
  maxLength = 280,
  warningThreshold = 0.85, // 85% of max length
  showRemaining = false,
  className = '',
  overLimitMessage = 'Character limit exceeded'
}) => {
  const remaining = maxLength - currentLength;
  const isOverLimit = currentLength > maxLength;
  const isNearLimit = currentLength / maxLength >= warningThreshold;

  // Determine text color based on status
  const getTextColor = () => {
    if (isOverLimit) return 'text-error';
    if (isNearLimit) return 'text-warning';
    return 'text-base-content/50';
  };

  // Format display text
  const getDisplayText = () => {
    if (showRemaining) {
      return remaining >= 0 ? `${remaining} left` : `${Math.abs(remaining)} over`;
    }
    return `${currentLength}/${maxLength}`;
  };

  return (
    <Box className={`flex items-center space-x-2 ${className}`}>
      <Text
        size="xs"
        className={getTextColor()}
      >
        {getDisplayText()}
      </Text>
      
      {isOverLimit && overLimitMessage && (
        <Text size="xs" className="text-error">
          {overLimitMessage}
        </Text>
      )}
      
      {/* Visual progress indicator */}
      <Box className="relative w-4 h-4">
        <svg
          className="w-4 h-4 transform -rotate-90"
          viewBox="0 0 24 24"
          fill="none"
        >
          {/* Background circle */}
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
            className="text-base-content/20"
          />
          {/* Progress circle */}
          <circle
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeDasharray={`${Math.min(currentLength / maxLength * 62.83, 62.83)} 62.83`}
            className={getTextColor()}
          />
        </svg>
      </Box>
    </Box>
  );
};

export default CharacterCounter;